import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import {
    EllipsisVerticalIcon,
    XMarkIcon,
    CurrencyRupeeIcon,
    ChevronDownIcon,
    FunnelIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PlusIcon
} from '@heroicons/react/24/outline';
import {
    EyeIcon,
    PencilSquareIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { TbFilterPause } from "react-icons/tb";
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@headlessui/react';
import { FaRegCopy } from 'react-icons/fa';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL;

const PlanSlap = ({ children }) => {
    const [searchParams] = useSearchParams();
    const operatorId = searchParams.get('operatorId');
    const planId = searchParams.get('planId');
    const providerId = searchParams.get('providerId');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [slaps, setSlaps] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [cardInfo, setCardInfo] = useState([]);
    const [access_data, setAccess_data] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSlap, setSelectedSlap] = useState(null);
    const [editedSlap, setEditedSlap] = useState(null);
    const [plans, setPlans] = useState([]);
    const [providers, setProviders] = useState([]);
    const [operatorSearchTerm, setOperatorSearchTerm] = useState('');
    const [operatorList, setOperatorList] = useState([]);
    const [isOperatorDropdownOpen, setIsOperatorDropdownOpen] = useState(false);
    const operatorSearchRef = useRef(null);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // Pagination state
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
    });

    const [filters, setFilters] = useState({
        status: '',
        plan_id: planId || '',
        provider_id: providerId || '',
        operator_id: operatorId || '',
        page: 1,
        limit: 10
    });

    const statusOptions = ['All', 'Active', 'Inactive'];

    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, []);

    useEffect(() => {
        if (access_data?.username) {
            fetchSlaps();
            fetchProvider();
            fetchPlan();
        }
    }, [access_data?.username]);

    useEffect(() => {
        if (filterDrawerOpen) {
            // disable scrolling
            document.body.style.overflow = "hidden";
        } else {
            // enable scrolling again
            document.body.style.overflow = "auto";
        }

        // cleanup when component unmounts
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [filterDrawerOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (operatorSearchRef.current && !operatorSearchRef.current.contains(event.target)) {
                setIsOperatorDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (operatorSearchTerm.trim() !== '') {
                fetchOperators(operatorSearchTerm);
            } else {
                setOperatorList([]);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [operatorSearchTerm]);

    const fetchOperators = async (searchTerm = '') => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    operator_name: searchTerm
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                return;
            }

            const response = await fetch(`${API_URL}get-operator-search.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                setOperatorList(result.data || []);
            } else {
                setOperatorList([]);
                toast.error(result.message || "Failed to fetch operators");
            }
        } catch (error) {
            toast.error("Error fetching operators");
            console.error("Operator fetch error:", error);
        }
    };

    const fetchPlan = async () => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'fetch-plan',
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                return;
            }

            const payloadToSend = {
                encrypted_key: encryptedMessage.encryptedKey,
                encrypted_data: encryptedMessage.encryptedData,
            };

            const response = await fetch(`${API_URL}plan-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify(payloadToSend),
            });

            const result = await response.json();

            if (result.status === true) {
                setPlans(result.data || []);
            } else {
                throw new Error(result.message || "Failed to fetch operators");
            }
        } catch (error) {
            toast.error("Error fetching operators");
        }
    }

    const fetchProvider = async () => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'fetch-provider',
                    category: '1'
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                return;
            }

            const payloadToSend = {
                encrypted_key: encryptedMessage.encryptedKey,
                encrypted_data: encryptedMessage.encryptedData,
            };

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify(payloadToSend),
            });

            const result = await response.json();

            if (result.status === true) {
                setProviders(result.data || []);
            } else {
                throw new Error(result.message || "Failed to fetch operators");
            }
        } catch (error) {
            toast.error("Error fetching operators");
        }
    }

    const fetchSlaps = async (filterParams = null) => {
        setLoading(true);
        try {
            const activeFilters = filterParams || filters;

            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'fetch-slaps',
                    status: activeFilters.status === 'All' ? '' : activeFilters.status,
                    plan_id: activeFilters.plan_id,
                    provider_id: activeFilters.provider_id,
                    operator_id: activeFilters.operator_id,
                    page: activeFilters.page,
                    limit: activeFilters.limit
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_URL}slap-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                setCardInfo(result.dashboard);

                const apiSlaps = result.data.map(slap => ({
                    id: slap.slap_id,
                    plan_id: slap.plan_id,
                    op_sr_id: slap.op_sr_id,
                    api_end_op_code: slap.api_end_op_code,
                    api_com_type: slap.api_com_type,
                    api_com: slap.api_com,
                    user_com_type: slap.user_com_type,
                    user_com: slap.user_com,
                    dist_com_type: slap.dist_com_type,
                    dist_com: slap.dist_com,
                    amount: slap.amount,
                    amounts: slap.amounts,
                    min_amount: slap.min_amount,
                    max_amount: slap.max_amount,
                    isRoffer: slap.isRoffer,
                    provider_id: slap.provider_id,
                    company_name: slap.company_name,
                    biller_name: slap.biller_name,
                    operator_logo: slap.operator_logo,
                    status: slap.status,
                    circle: slap.circle || '',
                    priority: slap.priority
                }));

                setSlaps(apiSlaps);

                setPagination({
                    currentPage: result.currentPage || 1,
                    totalPages: result.totalPages || 1,
                    totalItems: result.totalItems || apiSlaps.length,
                    itemsPerPage: result.limit || 10
                });
            } else {
                throw new Error(result.message || "Failed to fetch User data.");

                toast.error(result.message || "Failed to fetch commission slaps data.");
            }
        } catch (error) {
            toast.error(error.message || "An unexpected error occurred. Please try again.");
            setSlaps([]);
        } finally {
            setLoading(false);
        }
    };

    const openModal = (slap, copying = false) => {
        if (copying) {
            const newSlap = {
                ...slap,
                id: 'Auto-generated',
                plan_id: slap.plan_id,
                op_sr_id: slap.op_sr_id,
                provider_id: slap.provider_id
            };
            setSelectedSlap(newSlap);
            setEditedSlap(newSlap);
            setOperatorSearchTerm(slap.biller_name || '');
        } else if (!slap.id) {
            const newSlap = {
                id: 'Auto-generated',
                plan_id: planId || '',
                op_sr_id: operatorId || '',
                api_end_op_code: '',
                api_com_type: '1',
                api_com: '',
                user_com_type: '1',
                user_com: '',
                dist_com_type: '1',
                dist_com: '',
                amount: '',
                amounts: '',
                min_amount: '',
                max_amount: '',
                isRoffer: '0',
                provider_id: '',
                status: '1',
                priority: ''
            };
            setSelectedSlap(newSlap);
            setEditedSlap({ ...newSlap });
            setOperatorSearchTerm('');
        } else {
            setSelectedSlap(slap);
            setEditedSlap({ ...slap });
            setOperatorSearchTerm(slap.biller_name || '');
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedSlap(null);
        setEditedSlap(null);
        setOperatorSearchTerm('');
        setOperatorList([]);
    };

    const handleSaveClick = async () => {
        try {

            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: selectedSlap?.id && selectedSlap.id !== 'Auto-generated' ? 'update-slap' : 'add-slap',
                    op_sr_id: editedSlap.op_sr_id,
                    slap_id: editedSlap.id === 'Auto-generated' ? '' : editedSlap.id,
                    plan_id: editedSlap.plan_id,
                    api_end_op_code: editedSlap.api_end_op_code,
                    api_com_type: editedSlap.api_com_type,
                    api_com: editedSlap.api_com,
                    user_com_type: editedSlap.user_com_type,
                    user_com: editedSlap.user_com,
                    dist_com_type: editedSlap.dist_com_type,
                    dist_com: editedSlap.dist_com,
                    amount: editedSlap.amount,
                    amounts: editedSlap.amounts,
                    min_amount: editedSlap.min_amount,
                    max_amount: editedSlap.max_amount,
                    isRoffer: editedSlap.isRoffer,
                    provider_id: editedSlap.provider_id,
                    priority: editedSlap.priority
                }
            };

            console.log(dataToEncrypt);

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                return;
            }


            const response = await fetch(`${API_URL}slap-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                toast.success(selectedSlap?.id && selectedSlap.id !== 'Auto-generated'
                    ? "Commission slap updated successfully"
                    : "Commission slap added successfully");
                fetchSlaps(filters);
                closeModal();
            } else {
                toast.error(result.message || "Failed to save commission slap");
            }
        } catch (error) {
            console.error("Save commission slap error:", error);
            toast.error("An unexpected error occurred. Please try again.");
        }
    };

    const handleFieldChange = (field, value) => {
        setEditedSlap(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const applyFilters = () => {
        setFilterDrawerOpen(false);
        fetchSlaps({
            ...filters,
            page: 1
        });
    };

    const resetFilters = () => {
        const defaultFilters = {
            status: '',
            plan_id: '',
            provider_id: '',
            page: 1,
            limit: 10
        };
        setFilters(defaultFilters);
        fetchSlaps(defaultFilters);
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleStatusChange = async (slapId, newStatus) => {
        const statusValue = newStatus ? "1" : "0"; // "1" for active, "0" for inactive
        const statusText = newStatus ? "Active" : "Inactive";

        // Optimistic UI update
        setSlaps(prevSlaps =>
            prevSlaps.map(slap =>
                slap.id === slapId ? { ...slap, status: statusValue } : slap
            )
        );

        try {
            const response = await updateSlapStatus(slapId, statusValue);
            if (!response) throw new Error('Status update failed');

            toast.success(`Slap status updated to ${statusText}`);
        } catch (error) {
            // Revert on error
            setSlaps(prevSlaps =>
                prevSlaps.map(slap =>
                    slap.id === slapId ? { ...slap, status: slap.status === "1" ? "0" : "1" } : slap
                )
            );
            toast.error(error.message || 'Failed to update slap status');
        }
    };

    const updateSlapStatus = async (slapId, newStatus) => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'update-status',
                    slap_id: slapId,
                    status: newStatus
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                throw new Error("Encryption failed");
            }

            const response = await fetch(`${API_URL}slap-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();
            return result.status === true;
        } catch (error) {
            console.error('Slap status update error:', error);
            throw error;
        }
    };

    const copyText = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied successfully`);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setFilters(prev => ({
                ...prev,
                page: newPage
            }));
            fetchSlaps({
                ...filters,
                page: newPage
            });
        }
    };

    const toggleMobileSidebar = () => {
        setMobileSidebarOpen(!mobileSidebarOpen);
    };

    const deleteSlap = async (slapId) => {
        const confirm = await Swal.fire({
            title: 'Are you sure?',
            text: "Do you really want to delete this slap?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (!confirm.isConfirmed) return;

        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'delete-slap',
                    slap_id: slapId,
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                return;
            }

            const response = await fetch(`${API_URL}slap-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                toast.success("Plan slap deleted successfully");
                fetchSlaps();
            } else {
                toast.error(result.message || "Failed to delete plan slap");
            }
        } catch (error) {
            console.error("Delete plan slap error:", error);
            toast.error("An unexpected error occurred. Please try again.");
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isModalOpen]);

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
            <div className="flex flex-col flex-1">
                <Header />
                <main className="flex-1 overflow-auto">
                    {/* Filter Drawer */}
                    <AnimatePresence>
                        {filterDrawerOpen && (
                            <motion.div
                                initial={{ x: '-100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '-100%' }}
                                transition={{ type: 'tween' }}
                                className="fixed inset-y-0 left-0 z-50 w-80 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
                            >
                                <div className="p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-lg font-medium">Filter Slap</h2>
                                        <button
                                            onClick={() => setFilterDrawerOpen(false)}
                                            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                        >
                                            <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        {/* Status */}
                                        <div className="space-y-1">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                                            <Menu as="div" className="relative">
                                                <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                                                    <span>{filters.status || 'Select Status'}</span>
                                                    <ChevronDownIcon className="h-4 w-4" />
                                                </Menu.Button>
                                                <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                    {statusOptions.map((option) => (
                                                        <Menu.Item key={option}>
                                                            {({ active }) => (
                                                                <button
                                                                    onClick={() => handleFilterChange('status', option)}
                                                                    className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                                >
                                                                    {option}
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    ))}
                                                </Menu.Items>
                                            </Menu>
                                        </div>

                                        {/* Plan ID Dropdown */}
                                        <div className="space-y-1">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Plan</label>
                                            <Menu as="div" className="relative">
                                                <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                                                    <span>
                                                        {filters.plan_id
                                                            ? plans.find(p => p.id === filters.plan_id)?.name || filters.plan_id
                                                            : 'Select Plan'}
                                                    </span>
                                                    <ChevronDownIcon className="h-4 w-4" />
                                                </Menu.Button>
                                                <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none max-h-60 overflow-auto">
                                                    {plans.map((plan) => (
                                                        <Menu.Item key={plan.plan_id}>
                                                            {({ active }) => (
                                                                <button
                                                                    onClick={() => handleFilterChange('plan_id', plan.plan_id)}
                                                                    className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                                >
                                                                    {plan.plan_name} ({plan.plan_id})
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    ))}
                                                </Menu.Items>
                                            </Menu>
                                        </div>

                                        {/* Provider ID Dropdown */}
                                        <div className="space-y-1">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Provider</label>
                                            <Menu as="div" className="relative">
                                                <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                                                    <span>
                                                        {filters.provider_id
                                                            ? providers.find(p => p.id === filters.provider_id)?.name || filters.provider_id
                                                            : 'Select Provider'}
                                                    </span>
                                                    <ChevronDownIcon className="h-4 w-4" />
                                                </Menu.Button>
                                                <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none max-h-60 overflow-auto">
                                                    {providers.map((provider) => (
                                                        <Menu.Item key={provider.company_id}>
                                                            {({ active }) => (
                                                                <button
                                                                    onClick={() => handleFilterChange('provider_id', provider.company_id)}
                                                                    className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                                >
                                                                    {provider.company_name} ({provider.company_id})
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    ))}
                                                </Menu.Items>
                                            </Menu>
                                        </div>

                                        <div className="space-y-1">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Operator ID</label>
                                            <input
                                                type="text"
                                                value={filters.operator_id}
                                                onChange={(e) => handleFilterChange('operator_id', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                                                placeholder="Enter operator"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-6 flex space-x-3">
                                        <button
                                            onClick={resetFilters}
                                            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                                        >
                                            Reset
                                        </button>
                                        <button
                                            onClick={applyFilters}
                                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                        >
                                            Apply Filters
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Overlay when filter drawer is open */}
                    {filterDrawerOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-40"
                            onClick={() => setFilterDrawerOpen(false)}
                        />
                    )}

                    <main className="flex-1 p-6 overflow-auto">
                        <div className="max-w-7xl mx-auto">
                            <div className="flex items-center justify-between mb-6">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                                >
                                    <ChevronLeftIcon className="h-5 w-5 mr-1" />
                                    Back
                                </button>
                                <h2 className="text-xl font-semibold">
                                    Slap Management
                                </h2>
                                <div className='flex flex-row gap-3'>
                                    <button
                                        onClick={() => openModal({})}
                                        className="px-3 py-2 text-sm text-white bg-green-600 rounded hover:bg-green-700 flex items-center"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-1" />
                                        Add Operator
                                    </button>
                                    <button
                                        onClick={() => setFilterDrawerOpen(true)}
                                        className="p-2 rounded-full hover:bg-gray-300 bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
                                    >
                                        <TbFilterPause className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                    </button>
                                </div>
                            </div>
                            {children || (
                                <>
                                    {/* Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                                        {cardInfo.map((card, index) => (
                                            <div key={index} className={`${card.color} rounded-lg shadow p-6 text-white`}>
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <p className="text-sm font-medium opacity-80">{card.title}</p>
                                                        <h3 className="text-2xl font-bold mt-1">{card.count}</h3>
                                                    </div>
                                                    <div className="p-2 rounded-full bg-white bg-opacity-20">
                                                        {card.icon}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Commission Slaps Cards */}
                                    <div className="mb-6">
                                        {loading && (
                                            <div className="flex justify-center items-center py-12">
                                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                                            </div>
                                        )}

                                        {!loading && (
                                            <>
                                                {slaps.length > 0 ? (
                                                    <div className="overflow-x-auto rounded-lg shadow">
                                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                                                            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs uppercase">
                                                                <tr>
                                                                    <th className="px-3 py-2 text-left font-medium">Slap ID</th>
                                                                    <th className="px-3 py-2 text-left font-medium">Plan ID</th>
                                                                    <th className="px-3 py-2 text-left font-medium">Operator</th>
                                                                    <th className="px-3 py-2 text-center font-medium">Commission</th>
                                                                    <th className="px-3 py-2 text-center font-medium">Amount Range</th>
                                                                    <th className="px-3 py-2 text-center font-medium">Status</th>
                                                                    <th className="px-3 py-2 text-left font-medium">Provider</th>
                                                                    <th className="px-3 py-2 text-center font-medium">Priority</th>
                                                                    <th className="px-3 py-2 text-center font-medium">Roffer</th>
                                                                    <th className="px-3 py-2 text-center font-medium">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                                {slaps.map((slap) => (
                                                                    <tr key={slap.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">

                                                                        {/* Slap ID with Copy */}
                                                                        <td className="px-3 py-2 align-middle font-medium text-gray-900 dark:text-white">
                                                                            <div className="flex items-center gap-1">
                                                                                {slap.id}
                                                                                <button
                                                                                    onClick={() => copyText(slap.id, "Slap ID")}
                                                                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                                                    title="Copy Slap ID"
                                                                                >
                                                                                    <FaRegCopy className="h-3 w-3" />
                                                                                </button>
                                                                            </div>
                                                                        </td>

                                                                        {/* Plan ID */}
                                                                        <td className="px-3 py-2 align-middle text-gray-700 dark:text-gray-200">
                                                                            {slap.plan_id}
                                                                        </td>

                                                                        {/* Operator */}
                                                                        <td className="px-3 py-2 align-middle">
                                                                            <div className="flex items-center gap-2">
                                                                                <img
                                                                                    src={slap.operator_logo || "https://via.placeholder.com/24"}
                                                                                    alt={slap.biller_name}
                                                                                    className="w-8 h-8 rounded-full"
                                                                                />
                                                                                <span className="truncate">{slap.biller_name}</span>
                                                                            </div>
                                                                        </td>

                                                                        {/* Commission */}
                                                                        <td className="px-3 py-2 align-middle ">
                                                                            <div className="text-xs">
                                                                                <span className="font-medium">API:</span>{" "}
                                                                                {slap.api_com_type === "1" ? `${slap.api_com}%` : `₹${slap.api_com}`} <br />
                                                                                <span className="font-medium">User:</span>{" "}
                                                                                {slap.user_com_type === "1" ? `${slap.user_com}%` : `₹${slap.user_com}`} <br />
                                                                                <span className="font-medium">Agent:</span>{" "}
                                                                                {slap.dist_com_type === "1" ? `${slap.dist_com}%` : `₹${slap.dist_com}`}
                                                                            </div>
                                                                        </td>

                                                                        {/* Amount Range */}
                                                                        <td className="px-3 py-2 align-middle text-center">
                                                                            ₹{slap.min_amount} - ₹{slap.max_amount}
                                                                        </td>

                                                                        {/* Status */}
                                                                        <td className="px-3 py-2 align-middle text-center">
                                                                            <Switch
                                                                                checked={slap.status === "1"}
                                                                                onChange={(enabled) => handleStatusChange(slap.id, enabled)}
                                                                                className={`${slap.status === "1" ? "bg-green-500" : "bg-red-500"
                                                                                    } relative inline-flex h-5 w-9 items-center rounded-full`}
                                                                            >
                                                                                <span className="sr-only">Toggle status</span>
                                                                                <span
                                                                                    className={`${slap.status === "1" ? "translate-x-5" : "translate-x-1"
                                                                                        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                                                                />
                                                                            </Switch>
                                                                        </td>

                                                                        {/* Provider */}
                                                                        <td className="px-3 py-2 align-middle text-gray-700 dark:text-gray-200">
                                                                            {slap.company_name}
                                                                        </td>

                                                                        {/* Priority */}
                                                                        <td className="px-3 py-2 align-middle text-center">
                                                                            {slap.priority}
                                                                        </td>

                                                                        {/* Roffer */}
                                                                        <td className="px-3 py-2 align-middle text-center">
                                                                            <span
                                                                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${slap.isRoffer === "1"
                                                                                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                                                                        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                                                                    }`}
                                                                            >
                                                                                {slap.isRoffer === "1" ? "Yes" : "No"}
                                                                            </span>
                                                                        </td>

                                                                        {/* Actions */}
                                                                        <td className="px-3 py-2 align-middle text-center">
                                                                            <div className="flex justify-center gap-2">
                                                                                <button
                                                                                    onClick={() => openModal(slap)}
                                                                                    className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                                                                    title="Edit Slap"
                                                                                >
                                                                                    <PencilSquareIcon className="h-5 w-5 text-yellow-500" />
                                                                                </button>

                                                                                <button
                                                                                    onClick={() => deleteSlap(slap.id)}
                                                                                    className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                                                                    title="Delete Slap"
                                                                                >
                                                                                    <TrashIcon className="h-5 w-5 text-red-500" />
                                                                                </button>

                                                                                <button
                                                                                    onClick={() => openModal(slap, true)}
                                                                                    className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                                                                    title="Copy Slap"
                                                                                >
                                                                                    <FaRegCopy className="h-5 w-5 text-blue-500" />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                                        No commission slaps found
                                                    </div>
                                                )}

                                                {/* Pagination */}
                                                <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                                                    <div className="flex-1 flex justify-between sm:hidden">
                                                        <button
                                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                            disabled={pagination.currentPage === 1}
                                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                                                        >
                                                            Previous
                                                        </button>
                                                        <button
                                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                            disabled={pagination.currentPage === pagination.totalPages}
                                                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                                                        >
                                                            Next
                                                        </button>
                                                    </div>
                                                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                                        <div>
                                                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                                                Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> to{' '}
                                                                <span className="font-medium">
                                                                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                                                                </span>{' '}
                                                                of <span className="font-medium">{pagination.totalItems}</span> results
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                                <button
                                                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                                    disabled={pagination.currentPage === 1}
                                                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                                                >
                                                                    <span className="sr-only">Previous</span>
                                                                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                                                </button>

                                                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                                    let pageNum;
                                                                    if (pagination.totalPages <= 5) {
                                                                        pageNum = i + 1;
                                                                    } else if (pagination.currentPage <= 3) {
                                                                        pageNum = i + 1;
                                                                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                                                        pageNum = pagination.totalPages - 4 + i;
                                                                    } else {
                                                                        pageNum = pagination.currentPage - 2 + i;
                                                                    }

                                                                    return (
                                                                        <button
                                                                            key={pageNum}
                                                                            onClick={() => handlePageChange(pageNum)}
                                                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.currentPage === pageNum
                                                                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-gray-600 dark:text-white'
                                                                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                                                                                }`}
                                                                        >
                                                                            {pageNum}
                                                                        </button>
                                                                    );
                                                                })}

                                                                <button
                                                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                                    disabled={pagination.currentPage === pagination.totalPages}
                                                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                                                >
                                                                    <span className="sr-only">Next</span>
                                                                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                                                </button>
                                                            </nav>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </main>
                </main>
            </div>
            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <>
                        {/* Blurred Background Overlay */}
                        <motion.div
                            className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeModal}
                        />
                        <motion.div
                            className="fixed inset-0 z-50 overflow-y-auto"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <div className="flex items-center justify-center min-h-screen px-4 text-center sm:block sm:p-0">
                                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                                    &#8203;
                                </span>

                                <motion.div
                                    className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full relative z-50"
                                    initial={{ scale: 0.95, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.95, opacity: 0 }}
                                >
                                    <div className="bg-white dark:bg-gray-800 pb-4 flex flex-col" style={{ maxHeight: '90vh' }}>
                                        <div className="flex justify-between items-center border-b dark:border-gray-700 p-4 sticky top-0 bg-white dark:bg-gray-800 z-10 mb-2">
                                            <h3 className="text-lg font-semibold dark:text-white">{selectedSlap?.id ? `Edit Slap - ${selectedSlap.id}` : 'Add New Slap'}</h3>
                                            <button
                                                onClick={closeModal}
                                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                                            >
                                                <XMarkIcon className="h-6 w-6" />
                                            </button>
                                        </div>

                                        {/* Scrollable Content */}
                                        <div className="overflow-y-auto pr-2 -mr-2 flex-grow">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-2 px-4">
                                                {/* SLAP ID */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        SLAP ID
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-gray-200 py-2 px-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                        {editedSlap?.id || 'Auto-generated'}
                                                    </div>
                                                </div>

                                                {/* Plan ID Dropdown */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Plan
                                                    </label>
                                                    <Menu as="div" className="relative">
                                                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                                                            <span>
                                                                {editedSlap?.plan_id
                                                                    ? plans.find(p => p.id === editedSlap.plan_id)?.name || editedSlap.plan_id
                                                                    : 'Select Plan'}
                                                            </span>
                                                            <ChevronDownIcon className="h-4 w-4" />
                                                        </Menu.Button>
                                                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none max-h-60 overflow-auto">
                                                            {plans.map((plan) => (
                                                                <Menu.Item key={plan.plan_id}>
                                                                    {({ active }) => (
                                                                        <button
                                                                            onClick={() => handleFieldChange('plan_id', plan.plan_id)}
                                                                            className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                                        >
                                                                            {plan.plan_name} ({plan.plan_id})
                                                                        </button>
                                                                    )}
                                                                </Menu.Item>
                                                            ))}
                                                        </Menu.Items>
                                                    </Menu>
                                                </div>

                                                {/* Operator Service ID Search */}
                                                <div className="space-y-1" ref={operatorSearchRef}>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Operator Service
                                                    </label>
                                                    <div className="relative">
                                                        <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md shadow-sm">
                                                            <input
                                                                type="text"
                                                                value={operatorSearchTerm}
                                                                onChange={(e) => {
                                                                    setOperatorSearchTerm(e.target.value);
                                                                    setIsOperatorDropdownOpen(true);
                                                                }}
                                                                onFocus={() => setIsOperatorDropdownOpen(true)}
                                                                placeholder="Search operator..."
                                                                className="w-full px-3  bg-white dark:bg-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                            />
                                                            <button
                                                                onClick={() => setIsOperatorDropdownOpen(!isOperatorDropdownOpen)}
                                                                className="px-2 py-2 text-gray-400 hover:text-gray-500"
                                                            >
                                                                <ChevronDownIcon className="h-5 w-5" />
                                                            </button>
                                                        </div>

                                                        {isOperatorDropdownOpen && (
                                                            <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none max-h-60 overflow-auto">
                                                                {operatorList.length > 0 ? (
                                                                    operatorList.map((operator) => (
                                                                        <button
                                                                            key={operator.id}
                                                                            onClick={() => {
                                                                                handleFieldChange('op_sr_id', operator.operator_id);
                                                                                setOperatorSearchTerm(operator.biller_name);
                                                                                setIsOperatorDropdownOpen(false);
                                                                            }}
                                                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center"
                                                                        >
                                                                            <img
                                                                                src={operator.operator_logo || "https://via.placeholder.com/24"}
                                                                                alt={operator.biller_name}
                                                                                className="w-5 h-5 mr-2 rounded-full"
                                                                            />
                                                                            {operator.biller_name} ({operator.operator_id})
                                                                        </button>
                                                                    ))
                                                                ) : (
                                                                    <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                                                                        {operatorSearchTerm ? 'No operators found' : 'Start typing to search operators'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* API Operator Code */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        API Operator Code
                                                    </label>
                                                    <input
                                                        value={editedSlap?.api_end_op_code || ''}
                                                        onChange={(e) => handleFieldChange('api_end_op_code', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* API Commission Type */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        API Commission Type
                                                    </label>
                                                    <select
                                                        value={editedSlap?.api_com_type || '1'}
                                                        onChange={(e) => handleFieldChange('api_com_type', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="1" selected>Percentage</option>
                                                        <option value="2">Fixed</option>
                                                    </select>
                                                </div>

                                                {/* API Commission */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        API Commission
                                                    </label>
                                                    <input
                                                        value={editedSlap?.api_com || ''}
                                                        onChange={(e) => handleFieldChange('api_com', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* User Commission Type */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        User Commission Type
                                                    </label>
                                                    <select
                                                        value={editedSlap?.user_com_type || '1'}
                                                        onChange={(e) => handleFieldChange('user_com_type', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="1" selected>Percentage</option>
                                                        <option value="2">Fixed</option>
                                                    </select>
                                                </div>

                                                {/* User Commission */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        User Commission
                                                    </label>
                                                    <input
                                                        value={editedSlap?.user_com || ''}
                                                        onChange={(e) => handleFieldChange('user_com', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* Distributor Commission Type */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Distributor Commission Type
                                                    </label>
                                                    <select
                                                        value={editedSlap?.dist_com_type || '1'}
                                                        onChange={(e) => handleFieldChange('dist_com_type', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="1" selected>Percentage</option>
                                                        <option value="2">Fixed</option>
                                                    </select>
                                                </div>

                                                {/* Distributor Commission */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Distributor Commission
                                                    </label>
                                                    <input
                                                        value={editedSlap?.dist_com || ''}
                                                        onChange={(e) => handleFieldChange('dist_com', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* Amount */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Amount
                                                    </label>
                                                    <input
                                                        value={editedSlap?.amount || ''}
                                                        onChange={(e) => handleFieldChange('amount', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* Amounts */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Amounts
                                                    </label>
                                                    <input
                                                        value={editedSlap?.amounts || ''}
                                                        onChange={(e) => handleFieldChange('amounts', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* Min Amount */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Minimum Amount
                                                    </label>
                                                    <input
                                                        value={editedSlap?.min_amount || ''}
                                                        onChange={(e) => handleFieldChange('min_amount', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* Max Amount */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Maximum Amount
                                                    </label>
                                                    <input
                                                        value={editedSlap?.max_amount || ''}
                                                        onChange={(e) => handleFieldChange('max_amount', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* Is Recharge Offer */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Is Recharge Offer
                                                    </label>
                                                    <select
                                                        value={editedSlap?.isRoffer || '0'}
                                                        onChange={(e) => handleFieldChange('isRoffer', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="1">Yes</option>
                                                        <option value="0">No</option>
                                                    </select>
                                                </div>

                                                {/* Provider ID Dropdown */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Provider
                                                    </label>
                                                    <Menu as="div" className="relative">
                                                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                                                            <span>
                                                                {editedSlap?.provider_id
                                                                    ? providers.find(p => p.id === editedSlap.provider_id)?.name || editedSlap.provider_id
                                                                    : 'Select Provider'}
                                                            </span>
                                                            <ChevronDownIcon className="h-4 w-4" />
                                                        </Menu.Button>
                                                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none max-h-60 overflow-auto">
                                                            {providers.map((provider) => (
                                                                <Menu.Item key={provider.company_id}>
                                                                    {({ active }) => (
                                                                        <button
                                                                            onClick={() => handleFieldChange('provider_id', provider.company_id)}
                                                                            className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                                                        >
                                                                            {provider.company_name} ({provider.company_id})
                                                                        </button>
                                                                    )}
                                                                </Menu.Item>
                                                            ))}
                                                        </Menu.Items>
                                                    </Menu>
                                                </div>

                                                {/* Status */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Status
                                                    </label>
                                                    <select
                                                        value={editedSlap?.status || '1'}
                                                        onChange={(e) => handleFieldChange('status', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="1">Active</option>
                                                        <option value="0">Inactive</option>
                                                    </select>
                                                </div>

                                                {/* Priority */}
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                        Priority
                                                    </label>
                                                    <input
                                                        value={editedSlap?.priority || ''}
                                                        onChange={(e) => handleFieldChange('priority', e.target.value)}
                                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Footer with action buttons */}
                                        <div className="mt-4 pt-4 flex justify-end gap-3 px-4">
                                            <button
                                                onClick={closeModal}
                                                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleSaveClick}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            <ToastContainer />
        </div>
    );
};

export default PlanSlap;